import { CONSTANTS } from './constants'

export const RADAR_OPTIONS = {
  responsive: true,
  maintainAspectRatio: true,
  scale: {
    min: 1,
    max: 6,
    ticks: {
      stepSize: 1,
    },
    font: {
      family: 'Barlow, Helvetica, sans-serif',
      size: 10,
    },
  },
  scales: {
    r: {
      pointLabels: {
        font: {
          family: 'Barlow, Helvetica, sans-serif',
          size: 12,
          // style: 'italic'
        }
      }
    }
  },
  plugins: {
    title: {
      display: true,
      text: 'Title',
      font: {
        family: 'Barlow, Helvetica, sans-serif',
        size: 18,
      },
      color: '#000', // Optional: change the color of the title
      padding: {
        top: 10,
        bottom: 10,
      },
    },
    legend: {
      labels: {
        // This font property applies to legend labels
        font: {
          family: 'Barlow, Helvetica, sans-serif',
          size: 18,
        },
      },
    },
  },
}

export const barLegendMargin = {
  beforeInit(chart) {
    // console.log('chart.legend.fit:', chart.legend.fit)
    const fitValue = chart.legend.fit
    chart.legend.fit = function fit() {
      fitValue.bind(chart.legend)()
      return this.height += 30 // increases margin between legend and chart
    }
  },
}
export const BAR_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  scale: {
    min: 1,
    max: 6,
    ticks: {
      stepSize: 1,
    },
    font: {
      family: 'Barlow, Helvetica, sans-serif',
      size: 16,
    },
  },
  plugins: {
    title: {
      display: true,
      text: 'Title',
      font: {
        family: 'Barlow, Helvetica, sans-serif',
        size: 18,
      },
      color: '#000', // Optional: change the color of the title
      padding: {
        top: 10,
        bottom: 10,
      },
    },
    legend: {
      labels: {
        // This font property applies to legend labels
        font: {
          family: 'Barlow, Helvetica, sans-serif',
          size: 16,
        },
        // padding: 20, // increases margin above each line of the legend
      },
    },
  },
}

export const BORDER_THICKNESS = 2
const rgbaOpacity = ', 0.1)'
/**
 * called by ResultsModule1.js
 * @param jtLabels, object, 'label' portion of the jsonText object from Redux
 * @param chartLabels, [string], labels for each axis of chart, starting from top and going clockwise
 * @param npoData, [number], NPO survey data converted to percent
 * @param benchData, [number], benchmark of either NPO category or all NPOs converted to percent
 * @param resultsData, [number], percentile data of npoData compared with benchData
 * @param benchType, string, either 'cat' or 'all', depending on whether there was sufficient data for category benchmark
 * @param yearlyComp, object, { lastSurveyYear, selectedYear, benchType... } or undefined
 * @return object, the complete chart data for generating the chart
 *
 * Note: for yearly comparison, we use the following variables slightly differently:
 * npoData = benchmark data, either for all NPOs ('all') or only the NPOs of the same category ('cat'); (always either 50 or 100)
 * benchData = last survey results percentiles compared with above
 * resultsData = results of selected year's survey percentiles, also compared with npoData (or null)
 * benchType = always null
 */
// getDataForChart(jtLabels, chartLabels, npoData, benchData, resultsData, benchType, yearlyComp)
export const getDataForChart = (jtLabels, chartLabels, npoData, benchData, resultsData, benchType, yearlyComp) => {
  // clean out any unuseful data to keep chart lean and easily readable
  // TODO: after update to yearly comparison remove these functions if no longer necessary
  benchData = onlyPreserveValidData(benchData) // if no benchmark for category, do not include
  // benchType = onlyPreserveValidData(benchType) // if above is null, this is irrelevant
  resultsData = onlyPreserveValidData(resultsData) // if yearly comparison, this represents the results of the selected year which may be undefined

  const datasets = []

  const npoDataLabel = yearlyComp?.benchType === 'cat' ?
    jtLabels.benchmarkNPOsOfSameField ?? 'Benchmark of NPOs in Same Field'
    : jtLabels.yourNpoResults ?? 'Your NPO results'

  const benchDataLabel = yearlyComp?.lastSurveyYear ?
    `${jtLabels.yourNpoPercentile ?? 'Your NPO\'s Percentile'} ${yearlyComp?.lastSurveyYear}`
    : benchType === 'cat' ?
      jtLabels.benchmarkNPOsOfSameField ?? 'Benchmark of NPOs in Same Field'  // this is the normal case
      : jtLabels.benchmarkAllNPOs ?? 'Benchmark of all NPOs'  // this is for when there are insufficient category results

  const resultsDataLabel = yearlyComp?.selectedYear ?
    `${jtLabels.yourNpoPercentile ?? 'Your NPO\'s Percentile'} ${yearlyComp?.selectedYear}`
    : benchType === 'cat' ?
      jtLabels.yourPercentileNPOsSameField ?? 'Your Percentile - NPOs of Same Field' // this is the normal case
      : jtLabels.yourPercentileAllNPOs ?? 'Your Percentile - All NPOs' // this is for when there are insufficient category results

  if (npoData) datasets.push(
    {
      label: npoDataLabel,
      data: npoData,
      backgroundColor: CONSTANTS.colors.benchLogoYellow.replace(')', rgbaOpacity), // add transparency
      borderColor: CONSTANTS.colors.benchLogoYellow,
      borderWidth: BORDER_THICKNESS,
    },
  )
  if (benchData) datasets.push(
    {
      label: benchDataLabel,
      data: benchData,
      backgroundColor: CONSTANTS.colors.benchLogoOrange.replace(')', rgbaOpacity),
      borderColor: CONSTANTS.colors.benchLogoOrange,
      borderWidth: BORDER_THICKNESS,
    },
  )
  if (resultsData) datasets.push(
    {
      label: resultsDataLabel,
      data: resultsData,
      backgroundColor: CONSTANTS.colors.benchLogoAnthracite.replace(')', rgbaOpacity),
      borderColor: CONSTANTS.colors.benchLogoAnthracite,
      borderWidth: BORDER_THICKNESS,
    },
  )

  // console.log('datasets:', datasets)
  return {
    labels: chartLabels,
    datasets,
  }
}

/**
 * checks if a chart dataset contains valid data to remove irrelevant data from chart
 * @param data, array of percentiles|null values
 * @return {data|null} returns same array or null
 */
const onlyPreserveValidData = (data) => {
  if (!data) return null
  let validDataPresent = false
  for (const percentile of data) {
    if (typeof percentile === 'number') validDataPresent = true
  }
  return validDataPresent ? data : null
}

export const BENCH_ALL_BG = CONSTANTS.colors.benchLogoOrange.replace(')', ', 0.2)') // add transparency
export const BENCH_ALL_BORDER = CONSTANTS.colors.benchLogoOrange
export const BENCH_CAT_BG = CONSTANTS.colors.benchLogoRed.replace(')', ', 0.2)')
export const BENCH_CAT_BORDER = CONSTANTS.colors.benchLogoRed
export const NPO_VS_ALL_BG = CONSTANTS.colors.benchLogoRed.replace(')', ', 0.2)')
export const NPO_VS_ALL_BORDER = CONSTANTS.colors.benchLogoRed
export const NPO_VS_CAT_BG = CONSTANTS.colors.benchLogoBlue.replace(')', ', 0.2)')
export const NPO_VS_CAT_BORDER = CONSTANTS.colors.benchLogoBlue
